// PinEditPanel — bottom-sheet for editing an EXISTING pin.
//
// Opens when a worker taps a pin already on the map. Shows the assigned
// learner + clue preview + two destructive-ish actions:
//   - 更改位置: enters "awaiting new location" mode in the parent
//   - 移 除:    deletes the pin doc (hard delete)
//
// The parent MapMark owns the mode state machine; this panel just
// renders the right buttons and bubbles intent up.

import { Hairline, Han, MonoLabel, T, TextBtn } from "@goodbye/ui";
import { pinPhotos, type UserDoc, type WithId } from "@goodbye/firebase";
import { useRef, useState } from "react";
import type { MarkerWithPins } from "../hooks/useAdminSync";

export interface PinEditPanelProps {
  /** The marker (with pins[]) containing the pin being edited. */
  marker: MarkerWithPins;
  /** Which pin in `marker.pins` is selected. */
  pinId: string;
  users: WithId<UserDoc>[];
  /** Phase: pin selected, awaiting new location, or working. */
  phase:
    | "viewing"
    | "awaiting-new-location"
    | "confirming-move"
    | "removing";
  /** Pending new location (only set in "confirming-move" phase). */
  pendingMove: { lat: number; lng: number } | null;
  /** Total pins sharing this pin's placedBatchAt(含自己). 1 = 單人藏點、
   *  不顯示「· 移 除 同 批」按鈕。≥2 = 多人藏點、可選擇批刪。 */
  batchSize?: number;
  /** 只有藏照片區(瑞芳/大同)才顯示「藏照片」屬性切換。 */
  canToggleReward?: boolean;
  onStartMove: () => void;
  onConfirmMove: () => void;
  onRemove: () => void;
  /** Optional — only wired when batchSize >= 2 makes sense. */
  onRemoveBatch?: () => void;
  /** Toggle this pin's 「藏照片」(reward) attribute → next value. */
  onToggleReward?: (next: boolean) => void;
  /** Append clue photos to this existing pin. Resolves when written. */
  onAddPhotos?: (files: File[]) => Promise<void>;
  /** Remove a single clue photo by its URL (two-step confirm in the panel). */
  onRemovePhoto?: (url: string) => Promise<void>;
  onClose: () => void;
}

export function PinEditPanel({
  marker,
  pinId,
  users,
  phase,
  pendingMove,
  batchSize = 1,
  canToggleReward = false,
  onStartMove,
  onConfirmMove,
  onRemove,
  onRemoveBatch,
  onToggleReward,
  onAddPhotos,
  onRemovePhoto,
  onClose,
}: PinEditPanelProps) {
  const [confirmRemove, setConfirmRemove] = useState(false);
  const [confirmRemoveBatch, setConfirmRemoveBatch] = useState(false);
  const [addingPhotos, setAddingPhotos] = useState(false);
  const [photoError, setPhotoError] = useState(false);
  // 點縮圖 → 全螢幕大圖(跟學員端 BarClue 一致)。2026-05-30。
  const [lightbox, setLightbox] = useState<string | null>(null);
  // 2026-05-30:移除提示照的兩段確認 — 記哪張縮圖正在「待確認移除」。
  // 第一次點 × → 設此 url;第二次點同張的「✓」→ 真刪;點別處重置。
  const [confirmRemovePhoto, setConfirmRemovePhoto] = useState<string | null>(null);
  const [removingPhoto, setRemovingPhoto] = useState(false);
  const addPhotoInputRef = useRef<HTMLInputElement | null>(null);
  const pin = marker.pins.find((p) => p.id === pinId);
  const learner = users.find((u) => u.id === marker.userId);
  const canBatchRemove = batchSize >= 2 && !!onRemoveBatch;

  if (!pin) {
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        <Han size={11} color={T.ink500}>找不到這個標記。可能已被另一名工作員移除。</Han>
        <TextBtn onClick={onClose} mono={false} size={12} ls="0.25em">
          · 關 閉
        </TextBtn>
      </div>
    );
  }

  // ── awaiting-new-location: tell the worker to tap the map ──────────
  if (phase === "awaiting-new-location") {
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        <MonoLabel size={11}>更 改 位 置</MonoLabel>
        <Han size={13} color={T.ink900} lh={1.6}>
          請點地圖上新的位置。
        </Han>
        <Han size={11} color={T.ink500}>
          {learner?.name ?? marker.userId} · 第 {marker.pins.findIndex((p) => p.id === pinId) + 1} 個藏點
        </Han>
        <TextBtn
          onClick={onClose}
          mono={false}
          size={12}
          ls="0.25em"
          framed={false}
          color={T.ink500}
        >
          · 取 消
        </TextBtn>
      </div>
    );
  }

  // ── confirming-move: new location picked, show confirm/cancel ──────
  if (phase === "confirming-move" && pendingMove) {
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        <MonoLabel size={11}>確 認 新 位 置</MonoLabel>
        <Han size={13} color={T.ink900} lh={1.6}>
          將 {learner?.name ?? marker.userId} 的藏點移到這裡?
        </Han>
        <Han size={10} color={T.ink500} style={{ fontFamily: T.mono }}>
          {pendingMove.lat.toFixed(5)}, {pendingMove.lng.toFixed(5)}
        </Han>
        <div style={{ display: "flex", gap: 12 }}>
          <TextBtn
            onClick={onConfirmMove}
            mono={false}
            size={13}
            ls="0.3em"
            framed
          >
            · 確 認
          </TextBtn>
          <TextBtn
            onClick={onClose}
            mono={false}
            size={12}
            ls="0.25em"
            framed={false}
            color={T.ink500}
          >
            · 取 消
          </TextBtn>
        </div>
      </div>
    );
  }

  // ── removing: in-flight, show busy ─────────────────────────────────
  if (phase === "removing") {
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        <Han size={12} color={T.ink500}>· 移 除 中…</Han>
      </div>
    );
  }

  // ── viewing (default): show pin info + edit/remove actions ─────────
  // × 從按鈕列拔出來放標題列右側,44×44 tap target、字 22 ink-700、
  // 工作員藏到一半要關掉時不用對著 12px 灰色文字戳半天。
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10, position: "relative" }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <MonoLabel size={11}>已 藏 的 標 記</MonoLabel>
        <button
          type="button"
          onClick={onClose}
          aria-label="關閉"
          style={{
            background: "transparent",
            border: "none",
            padding: 0,
            margin: 0,
            cursor: "pointer",
            fontFamily: T.mono,
            fontSize: 22,
            color: T.ink700,
            lineHeight: 1,
            width: 44,
            height: 44,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            touchAction: "manipulation",
          }}
        >
          ×
        </button>
      </div>
      <Han size={15} color={T.ink900} lh={1.5}>
        {learner?.name ?? marker.userId}
      </Han>
      <Han size={11} color={T.ink500}>
        第 {marker.pins.findIndex((p) => p.id === pinId) + 1} 個 · {pin.clue || "(無提示)"}
        {pin.foundAt && " · 已被找到"}
      </Han>
      {/* 藏照片屬性切換(只在藏照片區):工作員放完才發現勾錯、可在此改。
          純 native checkbox、對齊 PinForm 的「藏照片?」樣式。 */}
      {canToggleReward && onToggleReward && (
        <label
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            padding: "4px 2px",
            cursor: "pointer",
            minHeight: 32,
            fontFamily: T.han,
            fontSize: 13,
            color: T.ink900,
          }}
        >
          <input
            type="checkbox"
            checked={
              (pin as { reward?: boolean }).reward === true
            }
            onChange={(e) => onToggleReward(e.target.checked)}
            style={{ width: 16, height: 16, margin: 0, accentColor: "#0E0E0E" }}
          />
          藏照片?
        </label>
      )}
      {/* 既有 pin 補加提示照(2026-05-30 多照片):顯示目前張數 +
          「加照片」file input(可多選)。上傳走 arrayUnion、不覆蓋既有。 */}
      {onAddPhotos && (
        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <input
            ref={addPhotoInputRef}
            type="file"
            accept="image/*"
            multiple
            style={{ display: "none" }}
            onChange={async (e) => {
              const files = e.target.files ? Array.from(e.target.files) : [];
              e.target.value = "";
              if (files.length === 0) return;
              setPhotoError(false);
              setAddingPhotos(true);
              try {
                await onAddPhotos(files);
              } catch {
                setPhotoError(true);
              } finally {
                setAddingPhotos(false);
              }
            }}
          />
          <Han size={12} color={T.ink500}>
            提示照 {pinPhotos(pin).length} 張
          </Han>
          {/* 縮圖橫排 — 正方形裁切、右下角放大圖示、點開全螢幕大圖,
              跟學員端 BarClue 一致。讓工作員確認自己藏的提示照長怎樣。 */}
          {pinPhotos(pin).length > 0 && (
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: 8,
                marginBottom: 2,
              }}
            >
              {pinPhotos(pin).map((url, i) => {
                const armed = confirmRemovePhoto === url;
                return (
                <div
                  key={url}
                  style={{ position: "relative", width: 64, height: 64, lineHeight: 0 }}
                >
                  <button
                    type="button"
                    onClick={() => setLightbox(url)}
                    aria-label={`放大提示照 ${i + 1}`}
                    style={{
                      width: 64,
                      height: 64,
                      padding: 0,
                      border: "none",
                      background: "transparent",
                      cursor: "pointer",
                      lineHeight: 0,
                      touchAction: "manipulation",
                      display: "block",
                    }}
                  >
                    <img
                      src={url}
                      alt={`提示照 ${i + 1}`}
                      style={{
                        width: 64,
                        height: 64,
                        objectFit: "cover",
                        border: `1px solid ${T.ink200}`,
                        display: "block",
                      }}
                    />
                    <span
                      aria-hidden
                      style={{
                        position: "absolute",
                        right: 3,
                        bottom: 3,
                        width: 20,
                        height: 20,
                        borderRadius: "50%",
                        background: "rgba(14,14,14,0.78)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <svg width="11" height="11" viewBox="0 0 16 16" aria-hidden>
                        <path
                          d="M2 6 V2 H6 M14 6 V2 H10 M2 10 V14 H6 M14 10 V14 H10"
                          fill="none"
                          stroke={T.paper}
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </span>
                  </button>
                  {/* 移除鈕(右上角)。兩段確認:第一次 × → armed(變寬底
                      「移除?」);第二次點 → 真刪。armed 時點別處 / 縮圖會重置。 */}
                  {onRemovePhoto && (
                    <button
                      type="button"
                      disabled={removingPhoto}
                      onClick={async (e) => {
                        e.stopPropagation();
                        if (!armed) {
                          setConfirmRemovePhoto(url);
                          return;
                        }
                        setRemovingPhoto(true);
                        try {
                          await onRemovePhoto(url);
                        } catch {
                          // 失敗就回到未 armed,讓工作員重試。
                        } finally {
                          setRemovingPhoto(false);
                          setConfirmRemovePhoto(null);
                        }
                      }}
                      aria-label={armed ? `確認移除提示照 ${i + 1}` : `移除提示照 ${i + 1}`}
                      style={{
                        position: "absolute",
                        top: -6,
                        right: -6,
                        minWidth: armed ? "auto" : 22,
                        height: 22,
                        padding: armed ? "0 7px" : 0,
                        borderRadius: armed ? 0 : "50%",
                        background: armed ? T.ink900 : "rgba(14,14,14,0.82)",
                        border: armed ? `1px solid ${T.ink900}` : "none",
                        color: T.paper,
                        fontFamily: armed ? T.han : T.mono,
                        fontSize: armed ? 11 : 15,
                        lineHeight: 1,
                        letterSpacing: armed ? "0.1em" : 0,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        cursor: "pointer",
                        touchAction: "manipulation",
                      }}
                    >
                      {armed ? "移 除?" : "×"}
                    </button>
                  )}
                </div>
                );
              })}
            </div>
          )}
          {addingPhotos ? (
            <Han size={12} color={T.ink500}>· 上傳中…</Han>
          ) : (
            <TextBtn
              onClick={() => addPhotoInputRef.current?.click()}
              mono={false}
              size={13}
              ls="0.25em"
              framed={false}
              color={T.ink700}
            >
              · 加照片(可多張)
            </TextBtn>
          )}
          {photoError && (
            <Han size={12} color={T.ink900}>· 上傳失敗,再試一次</Han>
          )}
        </div>
      )}
      <Hairline style={{ width: 24, background: T.ink200, marginTop: 2 }} />
      <div style={{ display: "flex", gap: 14, marginTop: 4, flexWrap: "wrap" }}>
        <TextBtn
          onClick={() => {
            setConfirmRemove(false);
            onStartMove();
          }}
          mono={false}
          size={13}
          ls="0.25em"
          framed
        >
          · 更 改 位 置
        </TextBtn>
        {confirmRemove ? (
          <TextBtn
            onClick={onRemove}
            mono={false}
            size={13}
            ls="0.25em"
            framed
            color={T.ink900}
          >
            · 確 定 移 除?
          </TextBtn>
        ) : (
          <TextBtn
            onClick={() => {
              setConfirmRemoveBatch(false);
              setConfirmRemove(true);
            }}
            mono={false}
            size={13}
            ls="0.25em"
            framed={false}
            color={T.ink500}
          >
            · 移 除
          </TextBtn>
        )}
        {/* 多人藏點(全員 / 多選)時、額外提供「· 移 除 全 部」批刪。
            同款兩段確認:第一次 arm、第二次 fire。「· 移 除」跟「· 移 除
            全 部」互斥 arming、避免雙態同時亮。 */}
        {canBatchRemove && (
          confirmRemoveBatch ? (
            <TextBtn
              onClick={onRemoveBatch}
              mono={false}
              size={13}
              ls="0.25em"
              framed
              color={T.ink900}
            >
              · 確 定 移 除 全 部 ({batchSize})?
            </TextBtn>
          ) : (
            <TextBtn
              onClick={() => {
                setConfirmRemove(false);
                setConfirmRemoveBatch(true);
              }}
              mono={false}
              size={13}
              ls="0.25em"
              framed={false}
              color={T.ink500}
            >
              · 移 除 全 部 ({batchSize})
            </TextBtn>
          )
        )}
      </div>

      {/* 全螢幕大圖 lightbox — 點縮圖開啟,跟學員端一致。黑底看圖慣例。 */}
      {lightbox && (
        <div
          role="dialog"
          aria-label="提示照大圖"
          onClick={() => setLightbox(null)}
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 2000,
            background: "rgba(0,0,0,0.92)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 16,
            touchAction: "manipulation",
          }}
        >
          <img
            src={lightbox}
            alt="提示照(大圖)"
            style={{
              maxWidth: "100%",
              maxHeight: "100%",
              width: "auto",
              height: "auto",
              objectFit: "contain",
            }}
          />
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setLightbox(null);
            }}
            aria-label="關閉大圖"
            style={{
              position: "absolute",
              top: "calc(env(safe-area-inset-top, 0px) + 8px)",
              right: 8,
              width: 44,
              height: 44,
              background: "transparent",
              border: "none",
              color: "#FAFAFA",
              fontFamily: T.mono,
              fontSize: 26,
              lineHeight: 1,
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              touchAction: "manipulation",
            }}
          >
            ×
          </button>
        </div>
      )}
    </div>
  );
}
